import * as React from 'react';
import type { AxiosError } from 'axios';
import axios from 'axios';
import useSWR from 'swr';
import { useSnackbar } from 'notistack';
import {
  Box, Grid, Dialog,
} from '@material-ui/core';
import SkillLevelTypeLookup from './SkillLevelTypeLookup';
import SkillLevelTypeAdd from './SkillLevelTypeAdd';
import type { ISkillLevelType, ISkillLevelGroup } from './SkillLevelTypeInterfaces';
import SkillLevelGroupLookup from './SkillLevelGroupLookup';
import SkillLevelGroupAdd from './SkillLevelGroupAdd';

const SkillLevelTypes: React.FC<unknown> = () => {
  const [upsertingSkillLevelType, setUpsertingSkillLevelType] = React.useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  // STATES
  const [showTypesModal, setShowTypesModal] = React.useState(false);
  const [showGroupsModal, setShowGroupsModal] = React.useState(false);
  const [interfaceType, setInterfaceType] = React.useState('Add' || 'Edit');

  // FORM VALUES
  const [skillLevelTypeFormValues, setSkillLevelTypeFormValues] = React.useState<ISkillLevelType>({
    name: null, group: undefined, level: undefined, inUse: false,
  });
  const [skillLevelUnEditedValues, setSkillLevelUnEditedValues] = React.useState<ISkillLevelType>({
    name: null, group: undefined, level: undefined, inUse: false,
  });
  const [skillLevelGroupFormValues, setSkillLevelGroupFormValues] = React.useState<ISkillLevelGroup>({ name: null, inUse: false });

  const typesFetcher = (url: string) => axios.get<ISkillLevelType[]>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching Skill Level Types:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });
  const { data: skillLevelTypesData, mutate } = useSWR(['/skillLevelType/GetSkillLevelTypes'], (url) => typesFetcher(url));

  const groupsFetcher = (url: string) => axios.get<ISkillLevelGroup[]>(url)
    .then((response) => response.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(`There was the following error when fetching Skill Level Types:
      ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
    });
  const { data: skillLevelGroupsData, mutate: groupsMutate } = useSWR(['/skillLevelType/GetSkillLevelGroups'], (url) => groupsFetcher(url));

  const resetTypesState = () => {
    setSkillLevelTypeFormValues({
      name: null, group: undefined, level: undefined, inUse: false,
    });
  };

  const handleNewTypeAddButton = () => {
    setShowTypesModal(true);
    setInterfaceType('Add');
    resetTypesState();
  };

  const resetGroupsState = () => {
    setSkillLevelGroupFormValues({ name: '', inUse: false });
  };

  const handleUpsertSkillLevelTypeBtnClick = () => {
    if (!skillLevelTypeFormValues.name || !skillLevelTypeFormValues.group || !skillLevelTypeFormValues.level) {
      enqueueSnackbar('Please fill out all the fields for the Skill Level Type.', { persist: false, variant: 'error' });
      return;
    }
    if (interfaceType === 'Edit') {
      if (skillLevelTypeFormValues?.inUse && skillLevelUnEditedValues.name !== skillLevelTypeFormValues.name) {
        enqueueSnackbar('Cannot modify the name of this Skill Level Type because it is currenlty being used in a Rule.', { persist: false, variant: 'error' });
        return;
      }
    }

    setUpsertingSkillLevelType(true);
    axios.post<ISkillLevelType>('/skillLevelType/upsert', skillLevelTypeFormValues)
      .then(() => {
        enqueueSnackbar(`Skill Level Type ${interfaceType === 'Add' ? 'added' : 'edited'}.`, { variant: 'success' });
        setUpsertingSkillLevelType(false);
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        resetTypesState();
        setShowTypesModal(false);
      })
      .catch((e: AxiosError) => {
        enqueueSnackbar(`There was the following error when editing Skill Level Type:
            ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        setUpsertingSkillLevelType(false);
      });
  };

  const handleTypeDeleteButton = () => {
    if (skillLevelTypeFormValues?.inUse) {
      enqueueSnackbar(`Cannot delete this Skill Level Type: ${JSON.stringify(skillLevelTypeFormValues?.name)}`, { persist: false, variant: 'error' });
      return;
    }
    if (skillLevelTypeFormValues?.value) {
      axios.post<ISkillLevelType>('/skillLevelType/delete', { value: skillLevelTypeFormValues.value })
        .then(() => {
          enqueueSnackbar('Skill Level Type deleted.', { variant: 'success' });
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when deleting Skill Level Type:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    } else {
      enqueueSnackbar('There is no Skill Level Type selected to delete');
    }
  };

  const handleNewGroupAddButton = () => {
    setShowGroupsModal(true);
    setInterfaceType('Add');
    resetGroupsState();
  };

  const handleGroupDeleteButton = () => {
    if (skillLevelGroupFormValues?.inUse) {
      enqueueSnackbar(`Cannot delete this Skill Level Group since its currently being used: ${JSON.stringify(skillLevelGroupFormValues?.name)}`, { persist: false, variant: 'error' });
      return;
    }
    if (skillLevelGroupFormValues?.value) {
      axios.post<ISkillLevelGroup>('/skillLevelType/deleteGroup', { value: skillLevelGroupFormValues.value })
        .then(() => {
          enqueueSnackbar('Skill Level Group deleted.', { variant: 'success' });
          groupsMutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when deleting Skill Level Group:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
        });
    } else {
      enqueueSnackbar('There is no Skill Level Group selected to delete');
    }
  };

  const typesCancelButton = () => {
    setSkillLevelTypeFormValues({
      name: null,
      inUse: false,
      group: undefined,
      level: undefined,
    });
    setShowTypesModal(false);
  };

  const groupsEditCancelButton = () => {
    setShowGroupsModal(false);
  };

  const handleAddSkillLevelGroupBtnClick = () => {
    if (!skillLevelGroupFormValues.name) {
      enqueueSnackbar('Skill Level Group Name is required to create a new Skill Level Group.', { persist: false, variant: 'error' });
    } else {
      setUpsertingSkillLevelType(true);
      axios.post<ISkillLevelGroup>('/skillLevelType/upsertGroup', skillLevelGroupFormValues)
        .then(() => {
          enqueueSnackbar(`Skill Level Group ${interfaceType === 'Add' ? 'added' : 'edited'}.`, { variant: 'success' });
          setUpsertingSkillLevelType(false);
          groupsMutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
          resetGroupsState();
          setShowGroupsModal(false);
        })
        .catch((e: AxiosError) => {
          enqueueSnackbar(`There was the following error when adding Skill Level Group:
        ${JSON.stringify(e?.response?.data)}`, { persist: false, variant: 'error' });
          setUpsertingSkillLevelType(false);
        });
    }
  };

  return (
    <>
      <Grid xs={12} md={8}>
        <Grid item>
          <Box py={1}>
            <SkillLevelTypeLookup
              addNewType={handleNewTypeAddButton}
              data={skillLevelTypesData || undefined}
              setInterfaceType={setInterfaceType}
              setShowTypesModal={setShowTypesModal}
              setSkillLevelUnEditedValues={setSkillLevelUnEditedValues}
              setSkillLevelTypeFormValues={setSkillLevelTypeFormValues}
              deleteSillLevelType={handleTypeDeleteButton}
            />
          </Box>
        </Grid>
        <Grid item>
          <Box py={4}>
            <SkillLevelGroupLookup
              data={skillLevelGroupsData || undefined}
              addNewGroup={handleNewGroupAddButton}
              setShowGroupsModal={setShowGroupsModal}
              setInterfaceType={setInterfaceType}
              setEditSkillGroupFormValues={setSkillLevelGroupFormValues}
              deleteSkillLevelGroup={handleGroupDeleteButton}
            />
          </Box>
        </Grid>
        <Dialog onClose={typesCancelButton} open={showTypesModal} fullWidth maxWidth="md">
          <SkillLevelTypeAdd
            formValues={skillLevelTypeFormValues}
            setFormValues={setSkillLevelTypeFormValues}
            secondaryButtonAction={typesCancelButton}
            primaryButtonAction={handleUpsertSkillLevelTypeBtnClick}
            interfaceType={interfaceType}
            loading={upsertingSkillLevelType}
          />
        </Dialog>
        <Dialog onClose={typesCancelButton} open={showGroupsModal} fullWidth maxWidth="md">
          <SkillLevelGroupAdd
            formValues={skillLevelGroupFormValues}
            setFormValues={setSkillLevelGroupFormValues}
            secondaryButtonAction={groupsEditCancelButton}
            primaryButtonAction={handleAddSkillLevelGroupBtnClick}
            interfaceType={interfaceType}
            loading={upsertingSkillLevelType}
          />
        </Dialog>
      </Grid>
    </>
  );
};

export default SkillLevelTypes;
